import { createFileRoute, useNavigate } from "@tanstack/react-router";
import type { Dispatch, SetStateAction } from "react";
import { useCallback, useState } from "react";

import { AccordionButton } from "@/components/Button/AccordionButton";
import ActionButton from "@/components/Button/ActionButton";
import { AttributeCard } from "@/components/Card/AttributeCard";
import Icon from "@/components/Icon/Icon";
import type { IconName } from "@/components/Icon/iconMap";
import { Grid, Spacer, VStack } from "@/components/Layout";
import { Text } from "@/components/Typography/Text";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { defaultNS, useTranslation } from "@/lib/i18n";

export const Route = createFileRoute("/_staff/equipment/add-select/")({
  component: EquipmentTypeSelection,
});

type CommonProps = {
  selectedEquipmentType: string | null;
  setSelectedEquipmentType: Dispatch<SetStateAction<string | null>>;
};

function EquipmentTypeSelection() {
  const [selectedEquipmentType, setSelectedEquipmentType] = useState<
    string | null
  >("equipment");

  return (
    <VStack className="mx-auto w-[1000px]">
      <Header />
      <Spacer variant="xs" />
      <EquipmentTypeRadioGroup
        selectedEquipmentType={selectedEquipmentType}
        setSelectedEquipmentType={setSelectedEquipmentType}
      />
      <Spacer variant="xxl" />
      <AdditionalOptions />
    </VStack>
  );
}

function Header() {
  const { t } = useTranslation(defaultNS, {
    keyPrefix: "pages.equipment.addSelect.header",
  });

  return (
    <VStack>
      <Spacer variant="xxl" />
      <Text variant="h2">{t("headerTitle")}</Text>
    </VStack>
  );
}

function EquipmentTypeRadioGroup({
  selectedEquipmentType,
  setSelectedEquipmentType,
}: CommonProps) {
  const { t } = useTranslation(defaultNS, {
    keyPrefix: "pages.equipment.addSelect.body",
  });

  const EQUIPMENT_TYPES: {
    id: string;
    title: string;
    requiredDetails: { iconName: IconName; text: string }[];
  }[] = [
    {
      id: "equipment",
      title: t("equipmentAndConsumables"),
      requiredDetails: [
        { iconName: "SquareDot", text: t("itemCard.equipmentName") },
        { iconName: "SquareDot", text: t("itemCard.equipmentType") },
        { iconName: "SquareDot", text: t("itemCard.equipmentItem") },
      ],
    },
  ];

  const navigate = useNavigate();

  const handleRadioChange = useCallback(
    (value: string) => {
      setSelectedEquipmentType(value);
    },
    [setSelectedEquipmentType],
  );

  const handleButtonClick = useCallback(() => {
    if (selectedEquipmentType) {
      navigate({
        to: "/equipment/add",
        hash: "basic",
      });
    }
  }, [selectedEquipmentType, navigate]);

  return (
    <>
      <Spacer variant="xl" />
      <Text variant="h4">{t("selectEquipmentType")}</Text>
      <Spacer variant="l" />
      <RadioGroup onValueChange={handleRadioChange} defaultValue="equipment">
        <Grid columns={2} rowGap={28} columnGap={24}>
          {EQUIPMENT_TYPES.map((type) => (
            <AttributeCard
              key={type.id}
              title={type.title}
              titleStyle="CardHeader"
              onClick={() => handleRadioChange(type.id)}
              actionButton={
                <RadioGroupItem
                  id={type.id}
                  value={type.id}
                  className="h-4 w-4"
                  checked={selectedEquipmentType === type.id}
                />
              }
              actionButtonPosition="top-left"
              actionButtonIsInner={false}
              className="cursor-pointer rounded-lg bg-neutral-fill outline-solid outline-1 -outline-offset-1 outline-neutral-muted-border shadow-md"
            >
              <VStack className="gap-3">
                <Text variant="labelSmall">
                  {t("itemCard.requiredDetails")}
                </Text>
                {type.requiredDetails.map((detail, index) => (
                  <Icon
                    key={`${detail.text}-${index}`}
                    iconName={detail.iconName}
                    text={detail.text}
                  />
                ))}
              </VStack>
            </AttributeCard>
          ))}
        </Grid>
      </RadioGroup>
      <Spacer variant="xl" />
      <ActionButton
        icon="FilePenIcon"
        className="w-48"
        onClick={handleButtonClick}
      >
        {t("fillOutDetails")}
      </ActionButton>
    </>
  );
}

function AdditionalOptions() {
  const { t } = useTranslation(defaultNS, {
    keyPrefix: "pages.equipment.addSelect.options",
  });
  const navigate = useNavigate();

  const handleCsvImportClick = useCallback(() => {
    navigate({ to: "/equipment/import-csv", hash: "upload-file" });
  }, [navigate]);

  return (
    <VStack className="w-full">
      <Text variant="labelSemiLarge">{t("description")}</Text>
      <Spacer size={24} />
      <VStack className="w-full">
        {/* CSV一括アップロード */}
        <AccordionButton
          icon={
            <Icon
              iconName="DatabaseBackup"
              text={t("bulkCsvUpload.title")}
              textColor="text-primary"
              colorVariant="primary"
            />
          }
          className="w-full"
        >
          <VStack className="gap-2">
            <div className="flex flex-col items-start gap-2 text-neutral-on-fill">
              <Text variant="p">{t("bulkCsvUpload.description")}</Text>
            </div>
            <div>
              <ActionButton
                size="sm"
                variant="default"
                icon="ArrowRightIcon"
                iconPosition="right"
                onClick={handleCsvImportClick}
              >
                {t("bulkCsvUpload.button")}
              </ActionButton>
            </div>
          </VStack>
        </AccordionButton>

        <Spacer size={24} />

        {/* 外部システム同期 - 開発中 */}
        <AccordionButton
          icon={
            <Icon
              iconName="DatabaseBackup"
              text={t("syncIT")}
              textColor="text-primary"
              colorVariant="primary"
            />
          }
          inDevelopment
          className="w-full"
        />
      </VStack>
    </VStack>
  );
}
